import { forwardRef, useMemo } from 'react';
import { clsx } from 'clsx';
import { NumberWithUnit, UnitOption } from './NumberWithVariableUnitTypes';
import { FormField } from './FormField';

interface NumberWithVariableUnitProps {
  label?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  value: NumberWithUnit;
  onChange: (value: NumberWithUnit) => void;
  units: UnitOption[];
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
}

export const NumberWithVariableUnit = forwardRef<HTMLInputElement, NumberWithVariableUnitProps>(
  (
    {
      label,
      error,
      required,
      placeholder = '0',
      value,
      onChange,
      units,
      disabled,
      min,
      max,
      step = 1,
      className,
    },
    ref
  ) => {
    // Valeur par défaut pour l'unité (première option)
    const defaultUnit = useMemo(() => units[0]?.key || null, [units]);

    // S'assurer qu'il y a toujours une unité sélectionnée
    const currentValue = useMemo(() => ({
      value: value?.value ?? null,
      unit: value?.unit ?? defaultUnit,
    }), [value, defaultUnit]);

    const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value === '' ? null : Number(e.target.value);
      onChange({
        value: newValue,
        unit: currentValue.unit,
      });
    };

    const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const raw = e.target.value;
      onChange({
        value: currentValue.value,
        unit: isNaN(Number(raw)) ? raw : Number(raw),
      });
    };

    return (
      <FormField
        label={label}
        error={error}
        required={required}
        name=""
      >
        <div className={clsx('flex gap-2', className)}>
          {/* Input pour le nombre */}
          <div className="flex-1">
            <input
              ref={ref}
              type="number"
              value={currentValue.value ?? ''}
              onChange={handleValueChange}
              placeholder={placeholder}
              disabled={disabled}
              min={min}
              max={max}
              step={step}
              className={clsx(
                "w-full px-4 py-4 rounded border text-sm font-normal text-gray-700 box-border",
                "placeholder:text-gray-500 placeholder:italic",
                "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
                "h-12",
                error ? "border-red-500" : "border-gray-300",
                disabled && "bg-gray-100 cursor-not-allowed"
              )}
            />
          </div>

          {/* Select pour l'unité */}
          <div className="w-32">
            <select
              value={currentValue.unit || ''}
              onChange={handleUnitChange}
              disabled={disabled}
              className={clsx(
                "w-full px-4 py-4 rounded border text-sm font-normal text-gray-700 box-border",
                "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
                "h-12",
                error ? "border-red-500" : "border-gray-300",
                disabled && "bg-gray-100 cursor-not-allowed"
              )}
            >
              {units.map((unit) => (
                <option key={unit.key} value={unit.key}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </FormField>
    );
  }
);

NumberWithVariableUnit.displayName = 'NumberWithVariableUnit';
